import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { partnersService } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PartnersTable = ({ title = 'Partner\'ler' }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  useEffect(() => {
    loadPartners();
  }, [pagination.page, pagination.limit, filters]);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: 'approved',
        ...filters
      };

      const response = await partnersService.getAllPartners(params);

      if (response.success) {
        setPartners(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Partner\'ler yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading partners:', error);
      toast.error('Partner\'ler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleToggleStatus = async (partner) => {
    const newStatus = partner.status === 'approved' ? 'suspended' : 'approved';
    const confirmMessage = newStatus === 'suspended' 
      ? `${partner.name} adlı partner'i askıya almak istediğinize emin misiniz?`
      : `${partner.name} adlı partner'i tekrar aktif etmek istediğinize emin misiniz?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const response = await partnersService.togglePartnerStatus(partner.id, newStatus);
    if (response.success) {
      toast.success(response.message || 'Durum güncellendi');
      loadPartners();
    } else {
      toast.error(response.error || 'Durum güncellenemedi');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') {
      return <span className="badge bg-success">Onaylandı</span>;
    } else if (status === 'suspended') {
      return <span className="badge bg-warning">Askıya Alındı</span>;
    }
    return <span className="badge bg-secondary">{status}</span>;
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">Göster</span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={pagination.limit}
            onChange={handlePageSizeChange}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <form className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="search"
              placeholder="Ara (Ad, Telefon, Email, Şirket)..."
              value={filters.search}
              onChange={handleSearch}
            />
          </form>
        </div>
      </div>

      <div className="card-body p-0">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">Partner bulunamadı</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="bg-light">
                <tr>
                  <th>S.L</th>
                  <th>Onay Tarihi</th>
                  <th>Ad Soyad</th>
                  <th>Telefon</th>
                  <th>Email</th>
                  <th>Şirket Adı</th>
                  <th>Müşteri Sayısı</th>
                  <th>Son Giriş</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner, index) => (
                  <tr key={partner.id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>{formatDate(partner.approved_at)}</td>
                    <td>{partner.name}</td>
                    <td>{partner.phone}</td>
                    <td>{partner.email || '-'}</td>
                    <td>{partner.company_name || '-'}</td>
                    <td>
                      <span className="badge bg-info">
                        {partner.active_customers || 0} / {partner.total_customers || 0}
                      </span>
                    </td>
                    <td>{formatDate(partner.last_login_at)}</td>
                    <td>{getStatusBadge(partner.status)}</td>
                    <td>
                      <button
                        className={`btn btn-sm ${partner.status === 'approved' ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleToggleStatus(partner)}
                        title={partner.status === 'approved' ? 'Askıya Al' : 'Aktif Et'}
                      >
                        <Icon icon={partner.status === 'approved' ? 'mdi:pause' : 'mdi:play'} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="card-footer border-top bg-base py-16 px-24">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <span className="text-sm text-secondary-light">
                Toplam {pagination.total} partner, Sayfa {pagination.page} / {pagination.totalPages}
              </span>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Önceki
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Sonraki
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersTable;

